"use client";
/**
 * The recommended composition: the coaching text carries the card.
 *
 * A catalog row has no poster. The list call publishes three video BOOLEANS and
 * no address, and a playable URL only arrives from a second, billed
 * per-exercise call — so on the mounted page one row of three has a picture and
 * a media-led default would open on two designed absences. What separates one
 * movement from another here is its name, its muscle, its equipment and its
 * execution, and all four are type.
 *
 * The video is still stated, as a capsule in the identity's trailing slot, so a
 * reader can tell "no clip exists" from "no clip has been resolved" without
 * opening anything.
 */
import type { ReactElement } from "react";
import {
  CresultcardIdentity,
  CresultcardStack,
  CRC_TILE_INSET,
  CresultcardDensity,
  CresultcardIdentitySlot,
  CresultcardStackDensity,
  CresultcardTilePadding,
  CresultcardTitleLines,
} from "@/components/ui/Cresultcard";
import {
  C_WORKOUT_CARD_MODIFIER,
  C_WORKOUT_CARD_STEP_LIMIT,
} from "./cworkoutcard.constants";
import {
  CworkoutcardMetaLine,
  CworkoutcardMove,
  CworkoutcardMuscleLine,
  CworkoutcardTypeChips,
} from "./CworkoutcardMeta";
import { CworkoutcardShell } from "./CworkoutcardShell";
import { CworkoutcardStateCapsule } from "./CworkoutcardStateCapsule";
import {
  CworkoutcardVariant,
  type CworkoutcardVariantProps,
} from "./cworkoutcard.types";

const CARD_INSET = CRC_TILE_INSET[CresultcardTilePadding.Regular];
/** The title yields; the state capsule holds its width. */
const CARD_TRUNCATION: readonly CresultcardIdentitySlot[] = [
  CresultcardIdentitySlot.Title,
];

export function CworkoutcardCoachingCard({
  data,
  moveReading,
  openControl,
}: CworkoutcardVariantProps): ReactElement {
  return (
    <CworkoutcardShell
      density={CresultcardDensity.Card}
      head={
        <div
          style={{ minWidth: 0, padding: CARD_INSET }}
        >
          <CresultcardIdentity
            openControl={openControl}
            secondary={
              <CworkoutcardMetaLine
                data={data}
              />
            }
            title={data.title}
            titleLines={CresultcardTitleLines.Two}
            trailing={
              <CworkoutcardStateCapsule
                data={data}
              />
            }
            truncationOrder={CARD_TRUNCATION}
          />
        </div>
      }
      modifier={C_WORKOUT_CARD_MODIFIER[CworkoutcardVariant.CoachingCard]}
      openable
      variant={CworkoutcardVariant.CoachingCard}
      body={
        <div
          style={{
            minWidth: 0,
            paddingInline: CARD_INSET,
            paddingBlockEnd: CARD_INSET,
          }}
        >
          <CresultcardStack
            density={CresultcardStackDensity.Regular}
          >
            <CworkoutcardMove
              data={data}
              limit={C_WORKOUT_CARD_STEP_LIMIT}
              reading={moveReading}
            />
            <CworkoutcardTypeChips
              data={data}
            />
          </CresultcardStack>
        </div>
      }
      foot={
        <div
          style={{
            minWidth: 0,
            paddingBlock: CARD_INSET,
            paddingInline: CARD_INSET,
          }}
        >
          <CworkoutcardMuscleLine
            data={data}
          />
        </div>
      }
    />
  );
}
